import { useEffect, useState, useMemo } from 'react';
import { Search, X, Loader2, FileText, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ImportableLine {
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  discount_pct: number;
  line_total: number;
}

interface DocRow {
  id: string;
  document_number: number;
  issue_date: string;
  total: number | null;
  doc_type_name: string;
  doc_type_prefix: string;
  client_name: string | null;
}

interface Props {
  companyId: string;
  onSelect: (lines: ImportableLine[], sourceDocId: string) => void;
  onClose: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DocumentPickerModal({ companyId, onSelect, onClose }: Props) {
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linesLoading, setLinesLoading] = useState(false);

  // ── Fetch recent documents ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('inventory_documents')
          .select(`
            id,
            document_number,
            issue_date,
            total,
            document_types ( name, prefix ),
            inventory_clients ( name )
          `)
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .limit(200);

        if (err) throw err;
        if (cancelled) return;

        const mapped: DocRow[] = (data ?? []).map((d: any) => ({
          id: d.id,
          document_number: d.document_number,
          issue_date: d.issue_date,
          total: d.total,
          doc_type_name: d.document_types?.name ?? '',
          doc_type_prefix: d.document_types?.prefix ?? '',
          client_name: d.inventory_clients?.name ?? null,
        }));
        setDocs(mapped);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'שגיאה בטעינת מסמכים');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // ── Filter documents by search text ─────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return docs;
    const q = search.trim().toLowerCase();
    return docs.filter(d =>
      d.doc_type_name.toLowerCase().includes(q) ||
      d.client_name?.toLowerCase().includes(q) ||
      `${d.doc_type_prefix}${d.document_number}`.toLowerCase().includes(q) ||
      String(d.document_number).includes(q)
    );
  }, [docs, search]);

  // ── Confirm selection: fetch lines and return ───────────────────────────
  const handleConfirm = async () => {
    if (!selectedId) return;
    setLinesLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('inventory_document_lines')
        .select(`
          product_id,
          quantity,
          unit_price,
          discount_pct,
          line_total,
          inventory_products ( name )
        `)
        .eq('document_id', selectedId)
        .order('line_no');

      if (err) throw err;

      const lines: ImportableLine[] = (data ?? []).map((l: any) => ({
        product_id: l.product_id,
        product_name: l.inventory_products?.name ?? '',
        quantity: l.quantity ?? 0,
        unit_price: l.unit_price ?? 0,
        discount_pct: l.discount_pct ?? 0,
        line_total: l.line_total ?? 0,
      }));

      onSelect(lines, selectedId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת שורות');
    } finally {
      setLinesLoading(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────
  const fmtDate = (iso: string) => {
    try {
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    } catch { return iso; }
  };
  const fmtPrice = (n: number | null) =>
    n != null ? `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
           dir="rtl" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            ייבוא שורות ממסמך קיים
          </h2>
          <button type="button" onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="חפש לפי סוג מסמך, מספר, או שם לקוח..."
              className="w-full pr-10 pl-3 py-2.5 text-sm rounded-xl border border-gray-200
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
            />
          </div>
        </div>

        {/* Document list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">טוען מסמכים...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-sm text-red-600">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">
              {search ? 'לא נמצאו מסמכים תואמים' : 'אין מסמכים'}
            </div>
          ) : (
            filtered.map(d => {
              const ref = `${d.doc_type_prefix}${d.document_number}`;
              const isSelected = selectedId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedId(isSelected ? null : d.id)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-right
                             transition-all mb-1
                             ${isSelected
                               ? 'bg-blue-50 border-2 border-blue-400 shadow-sm'
                               : 'bg-white hover:bg-gray-50 border-2 border-transparent'}`}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0
                                  ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {isSelected ? <Check className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900">{d.doc_type_name}</span>
                      <span className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        {ref}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span>{fmtDate(d.issue_date)}</span>
                      {d.client_name && <span>{d.client_name}</span>}
                      {d.total != null && <span className="font-medium">{fmtPrice(d.total)}</span>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex items-center gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedId || linesLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                       font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {linesLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />טוען שורות...</>
            ) : (
              <>
                <Check className="w-4 h-4" />
                ייבא שורות
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl font-medium text-sm text-gray-600
                       bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}
